import {
  VuexModule,
  Module,
  Mutation,
  Action,
  getModule,
} from 'vuex-module-decorators';
import Product from 'common/model/Product';
import { CoaQueryPayload, ProductCOAPayload } from '@/common/write.types';
import FileUploader from '@/common/fileUploader';
import ProductService from '@/common/api/product.service';
import ProductCOA from '@/common/model/ProductCOA';
import store from '../index';

@Module({ dynamic: true, store, name: 'product' })
class ProductModule extends VuexModule {
  service = new ProductService();

  products: Array<Product> = []

  productCOAs: Array<ProductCOA> = [];


  @Mutation
  ADD_PRODUCT(product: Product) {
    this.products.push(product);
  }

  @Mutation
  ADD_PRODUCT_COA(coa: ProductCOA) {
    this.productCOAs.push(coa);
  }

  @Mutation
  SET_PRODUCTS(products: Array<Product>) {
    this.products = products;
  }

  @Mutation
  SET_PRODUCT_COA(productCOAs: Array<ProductCOA>) {
    this.productCOAs = productCOAs;
  }

  @Mutation
  REMOVE_PRODUCT(product: Product) {
    const productIndex = this.products.indexOf(product);
    // Check if product exists in product array
    if (productIndex > -1) {
      // If product exists in array, remove it from the state.
      this.products.splice(productIndex, 1);
    }
  }


  @Action
  async createProduct(payload: any): Promise<void> {
    const productPayload = { ...payload.product };
    if (payload.imageFile) {
      productPayload.image = payload.imageFile.name;
    }
    if (payload.coaFile) {
      productPayload.pdf_url = payload.coaFile.name;
    }
    const response = await this.service.createProduct(productPayload);
    if (!response) { return; }

    // If no errors were thrown, upload the asset to S3 using the signed URL
    const { product } = response.data.data;
    const uploader = new FileUploader();

    if (payload.imageFile) {
      // The returned product.image is the signed S3 URL
      await uploader.sendToS3(payload.imageFile, product.image);
    }

    if (payload.coaFile) {
      // The returned product.pdf is the signed S3 URL for the COA
      await uploader.sendToS3(payload.coaFile, product.pdf_url);
    }


    // Finally, add the product to the current state.
    this.ADD_PRODUCT(product);
  }

  @Action
  async searchCoaByQuery(payload: CoaQueryPayload): Promise<void> {
    const response = await this.service.searchCoaByQuery(payload);
    if (!response) { return; }
    const { coas }: { coas: Array<ProductCOA> } = response.data.data;
    if (!coas) { return; }
    this.SET_PRODUCT_COA(coas);
  }


  @Action
  async fetchAllCoaByBrand(brandId: string): Promise<void> {
    const response = await this.service.fetchAllCoaByBrand(brandId);
    if (!response) { return; }
    const { coas }: { coas: Array<ProductCOA> } = response.data.data;
    if (!coas) { return; }
    this.SET_PRODUCT_COA(coas);
  }

  @Action
  async createProductCOA(payload: { coaPayload: ProductCOAPayload, pdfFile: File }): Promise<void> {
    const response = await this.service.createProductCOA(payload.coaPayload);
    if (!response) { return; }
    const { coa }: { coa: ProductCOA } = response.data.data;
    if (!coa) { return; }
    const uploader = new FileUploader();
    await uploader.sendToS3(payload.pdfFile, coa.pdf_file);
    this.ADD_PRODUCT_COA(coa);
  }

  @Action
  async deleteProduct(product: any): Promise<void> {
    const payload = { product_id: product.id }
    const response = await this.service.deleteProduct(payload);
    if (!response) {
      return;
    }
    this.REMOVE_PRODUCT(product)
  }

  @Action
  async updateProduct(payload: any): Promise<void> {
    const productPayload = { ...payload.product };
    if (payload.imageFile) {
      productPayload.image = payload.imageFile.name;
    }
    if (payload.coaFile) {
      productPayload.pdf_url = payload.coaFile.name;
    }
    const response = await this.service.updateProduct(productPayload);
    if (!response) { return; }

    const { product } = response.data.data;
    const uploader = new FileUploader();
    if (payload.imageFile) {
      // The returned product.image is the signed S3 URL
      await uploader.sendToS3(payload.imageFile, product.image);
    }
    if (payload.coaFile) {
      // The returned product.pdf_url is the signed S3 URL for the COA
      await uploader.sendToS3(payload.coaFile, product.pdf_url);
    }
  }
}

export default getModule(ProductModule);
